import {
  assistedHeardInOrder,
  assistedOtherPhrase,
  assistedVisibleWords,
  assistedWordChunks,
  englishSyllables,
  type ServerAssistedResultMessage,
} from '@orborix/shared-types';
import type { PublicScenarioObjective } from '../../../lib/scenariosApi.js';
import type { ScenarioStartContext } from '../../scenarios/scenarioTypes.js';
import type { AssistedCopy } from './assistedCopy.js';

/**
 * Lógica pura de la tarjeta de la charla asistida (sin React): qué se lee en la
 * notita de la burbuja, qué dice la tarjeta cuando no se acepta, y el karaoke en
 * dos marcas (lo que el transcriptor ya oyó + el cursor de voz). Portado del
 * prototipo de la #468 (`prototipo/app.mjs`, `prototipo/voz.mjs`).
 */

export type AssistedResult = Omit<ServerAssistedResultMessage, 'type'>;

/**
 * La notita de la burbuja del alumno al aceptar (lo que antes se leía en la
 * tarjeta: al acertar la tarjeta no tiene texto, sólo la celebración).
 */
export function acceptedBubbleNote(r: AssistedResult, copy: AssistedCopy): string {
  const base = copy.bubble.said;
  if (r.state === 'ok-warning') {
    const w = r.missing[0] ?? r.extra[0];
    if (!w) return base;
    return `${base} · ${r.missing.length > 0 ? copy.bubble.warnMissing(w) : copy.bubble.warnExtra(w)}`;
  }
  if (r.state === 'ok-restart') return `${base} · ${copy.bubble.restarted}`;
  return base;
}

/** Título y texto de la tarjeta cuando lo dicho NO se aceptó. Nunca «está mal». */
export function rejectionText(
  r: AssistedResult,
  copy: AssistedCopy,
  /** La frase modelo que tenía que decir. */
  model: string,
): { title: string; heard: string | null; text: string } {
  if (r.state === 'silence' || r.heard.trim() === '') {
    return { title: copy.card.titleSilence, heard: null, text: copy.card.textSilence };
  }
  if (assistedOtherPhrase(model, r.missing)) {
    return {
      title: copy.card.titleOther,
      heard: copy.card.heard(r.heard),
      text: copy.card.textOther,
    };
  }
  const swap = r.missing.length === 1 && r.extra.length === 1;
  const what = swap
    ? copy.card.swap(r.extra[0]!, r.missing[0]!)
    : r.missing.length > 0
      ? copy.card.missing(assistedWordChunks(model, r.marks))
      : copy.card.extra(r.extra.join(' '));
  return {
    title: copy.card.titleRepeat(swap),
    heard: copy.card.heard(r.heard),
    text: what,
  };
}

// ── Karaoke ─────────────────────────────────────────────────────────────────
// Dos marcas: fuerte = lo que el transcriptor en vivo YA oyó (su parcial contra
// la frase, con el diff de la vara, siempre en orden); tenue = el cursor de voz,
// que avanza al instante con el nivel del micrófono a ritmo de sílabas (el
// primer parcial tarda ~1,6 s desde que empezás a hablar: INFORME de #468
// § Karaoke). El cursor no sabe QUÉ dijiste, sólo cuánto hablaste: por eso es
// tenue.

/** Calibrado en el prototipo con las grabaciones aceptadas de Emmanuel (p10 de 3,1–4,3 síl/s). */
export const SYLLABLES_PER_SECOND = 3;
const VOICE_ABS = 0.008; // rms de una ventana de 50 ms
const VOICE_REL = 0.1; // …o el 10 % del pico del intento (un micrófono que llega bajo)
const PAUSE_MS = 300; // las pausas cortas entre sílabas cuentan como voz

/** Cuenta los ms de voz de un intento, una ventana de rms por vez. */
export interface VoiceCounter {
  window: (rms: number, windowMs?: number) => void;
  /** Sílabas que ya habría dicho alguien que habla a `SYLLABLES_PER_SECOND`. */
  syllables: () => number;
}

export function createVoiceCounter(): VoiceCounter {
  let peak = 0;
  let lastVoice = -Infinity;
  let t = 0;
  let ms = 0;
  return {
    window(rms, windowMs = 50) {
      peak = Math.max(peak, rms);
      if (rms > Math.max(VOICE_ABS, VOICE_REL * peak)) lastVoice = t;
      if (t - lastVoice <= PAUSE_MS) ms += windowMs;
      t += windowMs;
    },
    syllables: () => (ms / 1000) * SYLLABLES_PER_SECOND,
  };
}

export type KaraokeMark = 'heard' | 'voice' | null;

/**
 * La marca de cada palabra visible de la frase modelo. Fuerte lo que el
 * transcriptor ya oyó, EN ORDEN desde el principio (`assistedHeardInOrder`: la
 * escucha en vivo le suele errar a la primera palabra y encender sólo lo oído
 * dejaba un hueco al principio; Emmanuel, 16/10); tenue, más allá, si el cursor
 * de voz pasó la mitad de la palabra.
 */
export function karaokeMarks(model: string, partial: string, saidSyllables: number): KaraokeMark[] {
  return karaokeMarksUpTo(model, assistedHeardInOrder(model, partial), saidSyllables);
}

/**
 * Lo mismo con el prefijo ya calculado: fuertes las primeras `heard` palabras
 * visibles. El repaso le pasa el máximo de la toma, para que no retroceda
 * cuando el parcial se corrige (`useReviewLive`).
 */
export function karaokeMarksUpTo(
  model: string,
  heard: number,
  saidSyllables: number,
): KaraokeMark[] {
  let acc = 0;
  return assistedVisibleWords(model).map((w, i) => {
    const s = englishSyllables(w);
    const byVoice = s > 0 && acc + s / 2 <= saidSyllables;
    acc += s;
    return i < heard ? 'heard' : byVoice ? 'voice' : null;
  });
}

/** Contexto de arranque de un remonte de la escena («Probar solo»). */
export type SceneRestartState = Record<string, unknown> & { scenario: ScenarioStartContext };

/**
 * «Probar solo»: el MISMO arranque de la escena (en la de cierre de una unidad,
 * la unidad, su título y el «vengo del Programa»: sin eso dejaba de contar como
 * el cierre de la unidad), con la escena en modo solo. Nunca un «continuar».
 */
export function soloRestartState(raw: unknown, scenario: ScenarioStartContext): SceneRestartState {
  const prev =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const { resumeSessionId: _resume, ...rest } = prev;
  return { ...rest, scenario: { ...scenario, mode: 'solo' } };
}

/**
 * Los objetivos que busca la charla asistida, en orden: los principales y el
 * extra al final (marcado `bonus`). Todos practicados → la oferta de hacerla
 * solo (Emmanuel, 14/10, «En el médico»: sin el extra, la oferta salía con la
 * misión a medias); si el extra no entra, la oferta sale igual (`BONUS_TRIES`).
 */
export function assistedObjectives(
  objectives: ReadonlyArray<PublicScenarioObjective>,
): Array<{ id: string; label: string; bonus?: true }> {
  return [
    ...objectives.filter((o) => o.kind === 'primary').map((o) => ({ id: o.id, label: o.label })),
    ...objectives
      .filter((o) => o.kind === 'bonus')
      .map((o) => ({ id: o.id, label: o.label, bonus: true as const })),
  ];
}
